/**
 * Contract Pack wizard — admin generates and reviews a Helios contract pack,
 * pre-fills from CRM, prompts for manual fields, previews the merged PDF,
 * and sends to customer for signing via a 21-day public token.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, FileText, Send, Eye, ChevronRight, CheckCircle2, FileSignature, Plus, Trash2, AlertTriangle, Wand2, Battery, Upload, Paperclip, GripVertical, Sparkles, RefreshCw, ExternalLink, Download, House } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { PostCreateInvoiceDialog } from './PostCreateInvoiceDialog';
import { runProposalJob } from '../lib/proposalJob';

// Guard against showing a corrupt / truncated response as if it were a PDF.
// A merged contract pack can be 10-15 MB; if the response body is cut short
// (e.g. a proxy timeout) the browser still has a `%PDF` header but a missing
// tail, which the PDF viewer reports as "We can't open this file". We verify
// the blob actually starts with the PDF magic bytes and turn anything else
// into a readable error message rather than opening a broken file.
const blobToPdfUrl = async (blobData) => {
  const head = await blobData.slice(0, 5).text();
  if (!head.startsWith('%PDF')) {
    let detail = 'The server returned an invalid PDF';
    try {
      const text = await blobData.text();
      detail = text.slice(0, 200) || detail;
    } catch { /* noop */ }
    throw new Error(detail);
  }
  const blob = new Blob([blobData], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
};

const PACK_TYPE_LABELS = {
  solar_battery: 'Solar + Battery',
  solar_only: 'Solar Only',
  battery_only: 'Battery Only (no Performance Estimate)',
};

const STEP_LABELS = {
  type: 'Choose Pack',
  review: 'Auto-filled Values',
  manual: 'Manual Fields',
  battery: 'Battery Spec',
  upload: 'Supporting PDF',
  preview: 'Preview & Send',
};

const BATTERY_SPEC_FIELDS = [
  { key: 'system_type', label: 'EESS type', type: 'select', options: ['Off-The Shelf', 'Composite'] },
  { key: 'dimensions', label: 'Physical dimensions (W×D×H mm)', type: 'text', placeholder: '500x232x915' },
  { key: 'weight', label: 'Weight', type: 'text', placeholder: '128.8kg' },
  { key: 'coupling', label: 'AC or DC coupled?', type: 'select', options: ['DC Coupled', 'AC Coupled'] },
  { key: 'inverter_status', label: 'In-situ inverter being used?', type: 'select', options: ['New Inverter', 'Existing Inverter'] },
  { key: 'max_power_kw', label: 'Maximum power output (kW)', type: 'text', placeholder: '8.2' },
  { key: 'max_current_a', label: 'Maximum continuous current (A)', type: 'text', placeholder: '160' },
  { key: 'usable_capacity_kwh', label: 'Useable storage capacity (kWh, max DoD)', type: 'text', placeholder: '12.9' },
  { key: 'round_trip_efficiency', label: 'Round-trip efficiency (%)', type: 'text', placeholder: '95' },
  { key: 'grid_charge_loss_kwh', label: 'Energy loss if charged from grid (kWh)', type: 'text', placeholder: '0.65' },
  { key: 'replacement_module_cost', label: 'Cost of replacement battery modules (£)', type: 'text', placeholder: '2100' },
  { key: 'island_mode', label: 'Island mode? Max load in kW (or N/A)', type: 'text', placeholder: 'N/A' },
  { key: 'eess_class', label: 'EESS Classification (MIS 3012)', type: 'select', options: ['1', '2', '3', '4'] },
  { key: 'battery_type', label: 'Battery chemistry / type (incl. ventilation & fire safety)', type: 'text', placeholder: 'LiFePO4' },
  { key: 'soh_method', label: 'Method of indicating state-of-health', type: 'text', placeholder: 'On-Screen Menu' },
  { key: 'proposed_location', label: 'Proposed location', type: 'text', placeholder: 'Garage' },
];

// MCS performance estimate placeholders that should auto-populate the
// wizard's Manual Fields step. Module-level constant (stable identity) so the
// auto-fill effect below doesn't need it in its dependency array.
const MCS_PERFORMANCE_PLACEHOLDERS = [
  '[Installed Capacity]', '[orientation from south]', '[incline on roof]',
  '[Postcode Region]', '[kWh from table]', '[Shade Factor]',
  '[Estimated annual output]', '[Electricity consumption annual]',
  '[Expected solar PV only self consumption]', '[grid dependency pv only]',
  '[usable capacity from battery storage]',
  '[Expected solar & eess self consumption]', '[Grid independency combined]',
  '[EESS capacity not used for self-consumption]',
  '[Total energy discharged annually]', '[self-consumption percentage]',
  '[export percentage]', '[=pv and/or battery self consumption*0.28]',
  '[=[Estimated annual output]/100*[export percentage]*0.15]',
  '[total system price]', '[savings in total]',
  '[Payback period in years]', '[Payback Period Months]',
];

export const ContractPackWizard = ({ open, onClose, customerId, customerName, jobLabel, onSent }) => {
  const [step, setStep] = useState('type');
  const [packType, setPackType] = useState('solar_battery');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Deposit-invoice guard shown before sending a pack for signing.
  const [showDepositWarn, setShowDepositWarn] = useState(false);
  const [depositWarnAmount, setDepositWarnAmount] = useState(0);
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  const [depositCustomer, setDepositCustomer] = useState(null);
  const [autoFill, setAutoFill] = useState({});
  const [autoOverrides, setAutoOverrides] = useState({});
  // Raw mcs_solar_pv_data blob from the backend — used to compute the
  // equipment-field "auto-filled from proposal" pill state on Step 2.
  const [mcsSolarPvData, setMcsSolarPvData] = useState({});
  const [manualFields, setManualFields] = useState([]);
  const [manualValues, setManualValues] = useState({});
  const [extraLineItems, setExtraLineItems] = useState([]);
  const [vatRate, setVatRate] = useState(0);
  const [presets, setPresets] = useState([]);
  const [warrantyDefaults, setWarrantyDefaults] = useState([]);
  const [batteryPresets, setBatteryPresets] = useState([]);
  const [batterySpec, setBatterySpec] = useState({});
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [costCheck, setCostCheck] = useState(null);
  const [packId, setPackId] = useState(null);
  // Tracks the most-recent save timestamp so we can show a "Last saved …"
  // hint in the dialog header and reassure the admin that going back to
  // earlier steps + tweaking figures + hitting Regenerate will produce a
  // fresh PDF reflecting the current values.
  const [lastSaved, setLastSaved] = useState(null);
  const [draftStatus, setDraftStatus] = useState(null);  // 'draft' | null
  const [previewUrl, setPreviewUrl] = useState(null);
  const [bundleLoa, setBundleLoa] = useState(false);
  const [bundleLoaDno, setBundleLoaDno] = useState('GTC');
  const [bundleLoaAppType, setBundleLoaAppType] = useState('G99');
  // Supporting PDFs uploaded by admin (list of {id, filename, size_bytes, uploaded_at})
  const [userPdfs, setUserPdfs] = useState([]);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  // Proposal banner: shown when the wizard sees no MCS performance data on
  // the customer (i.e. no solar_proposal has been uploaded). Lets the admin
  // upload one in-place so the Performance Estimate group auto-fills.
  const [uploadingProposal, setUploadingProposal] = useState(false);
  const proposalInputRef = React.useRef(null);

  // Cut-out image gate — a contract cannot be SENT until the customer has a
  // cut-out image tagged in their Documents. Admins can save a draft without
  // one, but the Send button stays disabled + a banner explains why.
  const [cutoutStatus, setCutoutStatus] = useState(null);
  const [skipCutout, setSkipCutout] = useState(false);
  const [uploadingCutout, setUploadingCutout] = useState(false);
  const [cutoutPickerOpen, setCutoutPickerOpen] = useState(false);
  const cutoutInputRef = React.useRef(null);

  const loadCutout = useCallback(async () => {
    if (!customerId) return;
    try {
      const { data } = await api.get(`/customers/${customerId}/cutout-status`);
      setCutoutStatus(data);
    } catch {
      setCutoutStatus(null);
    }
  }, [customerId]);

  useEffect(() => {
    if (open && customerId) loadCutout();
  }, [open, customerId, loadCutout]);

  const handleUploadCutout = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploadingCutout(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('display_name', 'Cut-out image');
      const { data: doc } = await api.post(`/customers/${customerId}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await api.patch(`/customers/${customerId}/documents/${doc.id}/tag`, { tag: 'cutout_image' });
      toast.success('Cut-out image saved to customer documents');
      await loadCutout();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload cut-out image');
    } finally {
      setUploadingCutout(false);
    }
  };

  const handlePickCutout = async (docId) => {
    try {
      await api.patch(`/customers/${customerId}/documents/${docId}/tag`, { tag: 'cutout_image' });
      toast.success('Cut-out image set from customer documents');
      setCutoutPickerOpen(false);
      await loadCutout();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set cut-out image');
    }
  };

  // MCS performance estimate placeholders that should auto-populate the
  // wizard's Manual Fields step. When `auto_filled` brings values for these
  // (i.e. the customer has a solar_proposal uploaded), copy them into
  // `manualValues` so the admin sees them pre-filled. Admin can still edit.

  // Snapshot of MCS values originally extracted from the proposal — used by
  // the Manual Fields step to show an "Auto-filled" pill on untouched
  // fields and an "Edited (revert)" pill on ones the admin has changed.
  const [mcsExtracted, setMcsExtracted] = useState({});

  // Seed manualValues from auto_filled MCS data, only if empty (so we never
  // clobber what the admin already typed). Runs each time autoFill changes.
  useEffect(() => {
    if (!autoFill || Object.keys(autoFill).length === 0) return;
    setManualValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of MCS_PERFORMANCE_PLACEHOLDERS) {
        const v = autoFill[key];
        if (v !== undefined && v !== null && v !== '' && (next[key] === undefined || next[key] === '')) {
          next[key] = String(v);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // Track the original extracted values for the audit-trail pills
    setMcsExtracted((prev) => {
      const snap = { ...prev };
      for (const key of MCS_PERFORMANCE_PLACEHOLDERS) {
        const v = autoFill[key];
        if (v !== undefined && v !== null && v !== '') {
          snap[key] = String(v);
        }
      }
      return snap;
    });
  }, [autoFill]);

  // Helper for the Manual Fields renderer — given a placeholder, decide
  // whether the field originated from the proposal extraction (pill state).
  const mcsPillState = (placeholder) => {
    const extracted = mcsExtracted[placeholder];
    if (extracted === undefined) return null;
    const current = manualValues[placeholder];
    if (current === undefined || current === '' || current === extracted) {
      return { kind: 'auto', extracted };
    }
    return { kind: 'edited', extracted };
  };

  // Equipment placeholders (Step 2 "Auto-filled Values") that originate
  // from the customer's mcs_solar_pv_data proposal extraction. Pill state
  // compares the auto-fill (or admin override) against the proposal value.
  const EQUIPMENT_PROPOSAL_MAP = {
    '[Solar Panels Manufacturer]': 'panel_manufacturer',
    '[Solar Panel Model]': 'panel_model',
    '[Solar Panel qty]': 'panel_count',
    '[Inverter Manufacturer]': 'inverter_manufacturer',
    '[Inverter Model]': 'inverter_model',
    '[Battery Manufacturer]': 'battery_manufacturer',
    '[Battery Model]': 'battery_model',
    '[Total System Cost]': 'purchase_price_gbp',
  };

  const equipmentPillState = (placeholder) => {
    const mcsKey = EQUIPMENT_PROPOSAL_MAP[placeholder];
    if (!mcsKey) return null;
    const proposalVal = mcsSolarPvData?.[mcsKey];
    if (proposalVal === undefined || proposalVal === null || proposalVal === '') return null;
    // Normalise both sides to a comparable string. Currency / numeric
    // fields the auto-fill may render with currency formatting — strip
    // commas, currency, decimal-zero noise before comparing.
    const norm = (v) => String(v ?? '').replace(/[£,\s]/g, '').replace(/\.0+$/, '').toLowerCase();
    const current = autoOverrides[placeholder] !== undefined ? autoOverrides[placeholder] : autoFill[placeholder];
    const extractedStr = String(proposalVal);
    if (current === undefined || current === '' || norm(current) === norm(extractedStr)) {
      return { kind: 'auto', extracted: extractedStr };
    }
    return { kind: 'edited', extracted: extractedStr };
  };

  // Detect "no proposal uploaded" — used to show the upload banner above
  // the wizard so the admin can attach one without leaving the dialog.
  const hasMcsAutoFill = !!autoFill?.['[Installed Capacity]'];
  const needsProposalUpload = !hasMcsAutoFill
    && (packType === 'solar_battery' || packType === 'solar_only');

  const handleProposalUploadInWizard = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF proposal');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast.error('Proposal PDF exceeds 30 MB');
      try { e.target.value = ''; } catch { /* noop */ }
      return;
    }
    setUploadingProposal(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await runProposalJob(
        api,
        `/dno/extract-from-proposal/start?customer_id=${encodeURIComponent(customerId)}`,
        fd,
      );
      // Refresh the wizard's auto-fill so the new MCS values appear
      const { data } = await api.get(
        `/customers/${customerId}/contract-pack/auto-fill`,
        { params: { pack_type: packType } },
      );
      setAutoFill(data.auto_filled || {});
      setManualFields(data.manual_fields || []);
      setMcsSolarPvData(data.mcs_solar_pv_data || {});
      toast.success('Proposal attached — Performance Estimate auto-filled');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Proposal upload failed');
    } finally {
      setUploadingProposal(false);
      try { e.target.value = ''; } catch { /* noop */ }
    }
  };

  const isBatteryPack = packType === 'solar_battery' || packType === 'battery_only';

  // Load shared lookups once when wizard opens
  useEffect(() => {
    if (!open) return;
    // Start every fresh open at pack-type selection so a previous job's
    // preview/step never carries over.
    setStep('type');
    setSelectedPresetId('');
    setPreviewUrl(null);
    api.get('/hardware-presets').then(({ data }) => setPresets(data || [])).catch(() => {});
    api.get('/manufacturer-warranty-defaults').then(({ data }) => setWarrantyDefaults(data || [])).catch(() => {});
    api.get('/battery-presets').then(({ data }) => setBatteryPresets(data || [])).catch(() => {});
  }, [open, customerId]);

  // Auto-fill warranty fields from manufacturer defaults whenever autoFill changes
  // (only fills if the admin hasn't already typed a value).
  useEffect(() => {
    if (!warrantyDefaults.length || !autoFill) return;
    const lookup = (mfr, cat) => warrantyDefaults.find(
      (w) => (w.manufacturer || '').toLowerCase() === (mfr || '').toLowerCase() && w.category === cat,
    )?.warranty_years;
    setManualValues((prev) => {
      const next = { ...prev };
      const map = [
        ['[Solar Panels Warranty]', autoFill['[Solar Panels Manufacturer]'] || autoFill['[Solar Panel Manufacturer]'], 'panel'],
        ['[Inverter Warrranty]', autoFill['[Inverter Manufacturer]'], 'inverter'],
        ['[Battery Warranty]', autoFill['[Battery Manufacturer]'], 'battery'],
        ['[Mounting System Warranty]', prev['[Mounting System Manufacturer]'], 'mounting'],
      ];
      for (const [key, mfr, cat] of map) {
        if (!next[key] && mfr) {
          const yrs = lookup(mfr, cat);
          if (yrs) next[key] = String(yrs);
        }
      }
      return next;
    });
  }, [autoFill, warrantyDefaults]);

  // Auto-load battery spec from preset when the customer's battery model matches
  useEffect(() => {
    if (!isBatteryPack || !batteryPresets.length || !autoFill) return;
    const mfr = (autoFill['[Battery Manufacturer]'] || '').toLowerCase().trim();
    const model = (autoFill['[Battery Model]'] || '').toLowerCase().trim();
    if (!mfr && !model) return;
    const match = batteryPresets.find(
      (b) => (b.manufacturer || '').toLowerCase().trim() === mfr && (b.model || '').toLowerCase().trim() === model,
    );
    if (match) {
      const { id, manufacturer, model: _m, created_at, created_by, updated_at, updated_by, ...spec } = match;
      // only auto-fill empty fields
      setBatterySpec((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(spec)) {
          if (!next[k]) next[k] = spec[k];
        }
        return next;
      });
      // Reflect the auto-match in the dropdown so the user can see which preset
      // is active and pick a different one if they want.
      setSelectedPresetId((cur) => cur || match.id || '');
    }
  }, [autoFill, batteryPresets, isBatteryPack]);

  const applyBatteryPreset = (preset) => {
    const { id, manufacturer, model, created_at, created_by, updated_at, updated_by, ...spec } = preset;
    setBatterySpec(spec);
    setSelectedPresetId(preset.id || '');
    toast.success(`Loaded preset: ${preset.manufacturer} ${preset.model}`);
  };

  // Load auto-fill data when pack type changes
  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true);
    api.get(`/customers/${customerId}/contract-pack/auto-fill`, { params: { pack_type: packType } })
      .then(({ data }) => {
        setAutoFill(data.auto_filled || {});
        setManualFields(data.manual_fields || []);
        setMcsSolarPvData(data.mcs_solar_pv_data || {});
        // Always reset draft-derived state to this job's values (or defaults).
        // Without the fallbacks, switching to a job that has NO draft would
        // silently retain the PREVIOUS job's manual values, line items and —
        // critically — its packId, so edits landed on the wrong job's pack.
        const d = data.last_draft || {};
        setManualValues(d.manual_fields || {});
        setExtraLineItems(d.extra_line_items || []);
        // Draft stores vat as a FRACTION (0.2); customer_vat_rate is a PERCENT (20).
        setVatRate(d.vat_rate !== undefined ? (d.vat_rate || 0) : ((data.customer_vat_rate || 0) / 100));
        setBatterySpec(d.battery_spec || {});
        setPackId(d.id || null);
        setLastSaved(d.updated_at || null);
        setDraftStatus(d.status || null);
        setPreviewUrl(null);
        setCostCheck(null);
        // Hydrate supporting-PDF list. Falls back to the legacy single-file
        // field for old drafts created before multi-file support.
        if (Array.isArray(d.user_pdfs) && d.user_pdfs.length) {
          setUserPdfs(d.user_pdfs);
        } else if (d.user_pdf_name) {
          setUserPdfs([{
            id: 'legacy',
            filename: d.user_pdf_name,
            size_bytes: d.user_pdf_size,
            uploaded_at: d.user_pdf_uploaded_at,
          }]);
        } else {
          setUserPdfs([]);
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load auto-fill'))
      .finally(() => setLoading(false));
  }, [open, customerId, packType]);

  // Cost discrepancy check — API call (debounced) keeps the persisted
  // reconciliation fresh for the generate/confirm flow. Live feedback below
  // is computed client-side for instant response as the admin types.
  const runCostCheck = useCallback(async () => {
    try {
      const { data } = await api.post(`/customers/${customerId}/contract-pack/cost-check`, {
        pack_type: packType,
        manual_fields: manualValues,
        extra_line_items: extraLineItems,
        vat_rate: vatRate,
      });
      setCostCheck(data);
      return data;
    } catch {
      // silent
      return null;
    }
  }, [customerId, packType, manualValues, extraLineItems, vatRate]);

  // Debounced so we don't fire an API call on every keystroke.
  useEffect(() => {
    if (step !== 'manual') return;
    const t = setTimeout(() => runCostCheck(), 400);
    return () => clearTimeout(t);
  }, [step, runCostCheck]);

  // Live (client-side) cost reconciliation — mirrors backend `cost_check` so
  // the total + field colours update instantly as the admin types the
  // hardware / scaffolding / installation costs.
  const parseMoney = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? 0 : n;
  };
  const crmTotal = costCheck?.crm_total ?? 0;
  const liveCost = useMemo(() => {
    const scaffolding = parseMoney(manualValues['[Cost of scaffolding]']);
    const install = parseMoney(manualValues['[Cost of installation]']);
    const extras = (extraLineItems || []).reduce(
      (s, ex) => s + parseMoney(ex?.unit_price) * (parseInt(parseMoney(ex?.qty), 10) || 1), 0);
    let hardware = parseMoney(manualValues['[Cost of hardware]']);
    if (hardware === 0 && crmTotal > 0) hardware = Math.max(0, crmTotal - scaffolding - install - extras);
    const subtotal = hardware + extras + scaffolding + install;
    const delta = Math.round((subtotal - crmTotal) * 100) / 100;
    const mismatch = crmTotal > 0 && Math.abs(delta) > 1.0;
    const suggested = Math.max(0, crmTotal - scaffolding - install - extras);
    // Grand total (inc VAT) is the customer-facing figure. vatRate is a
    // fraction (0.2). The quote uses the customer file total as the grand
    // total, so the "matched" figure is crm_total × (1+VAT); show the live
    // line-item grand total too so it converges as costs reconcile.
    const vr = vatRate && vatRate > 1 ? vatRate / 100 : (vatRate || 0);
    const liveGrandTotal = Math.round(subtotal * (1 + vr) * 100) / 100;
    const crmGrandTotal = Math.round(crmTotal * (1 + vr) * 100) / 100;
    return { subtotal, crmTotal, delta, mismatch, suggested, hasCrm: crmTotal > 0, vr, liveGrandTotal, crmGrandTotal };
  }, [manualValues, extraLineItems, crmTotal, vatRate]);

  const COST_FIELDS = React.useMemo(
    () => new Set(['[Cost of hardware]', '[Cost of scaffolding]', '[Cost of installation]']), []);
  const costFieldClass = (placeholder) => {
    if (!COST_FIELDS.has(placeholder) || !liveCost.hasCrm) return '';
    return liveCost.mismatch
      ? 'border-2 border-rose-400 bg-rose-50 focus-visible:ring-rose-400'
      : 'border-2 border-emerald-400 bg-emerald-50 focus-visible:ring-emerald-400';
  };

  const applyCrmTotal = () => {
    if (!liveCost.hasCrm) return;
    setManualValues((p) => ({ ...p, '[Cost of hardware]': String(liveCost.suggested) }));
    toast.success(`Hardware cost set to ${liveCost.suggested.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}`);
  };

  const groupedManualFields = useMemo(() => {
    const groups = {};
    for (const f of manualFields) {
      if (!groups[f.group]) groups[f.group] = { label: f.group_label, fields: [] };
      groups[f.group].fields.push(f);
    }
    return groups;
  }, [manualFields]);

  const overrideAuto = (placeholder, value) => {
    setAutoOverrides((p) => ({ ...p, [placeholder]: value }));
  };
  const setManual = (placeholder, value) => {
    setManualValues((p) => ({ ...p, [placeholder]: value }));
  };

  const saveDraft = async () => {
    // If we're editing a `sent` contract (admin is updating figures before
    // the customer has signed), PATCH that specific pack so we don't end
    // up creating a parallel draft alongside the sent one. The PATCH
    // endpoint refuses to update once status moves to customer_signed /
    // fully_signed — at that point the wizard surfaces a "Raise Contract
    // Variation" CTA instead.
    if (packId && draftStatus === 'sent') {
      const { data } = await api.patch(`/customers/${customerId}/contract-pack/${packId}`, {
        manual_fields: manualValues,
        auto_overrides: autoOverrides,
        extra_line_items: extraLineItems,
        vat_rate: vatRate,
        battery_spec: batterySpec,
        reset_customer_views: true,
      });
      setPackId(data.id);
      setLastSaved(data.updated_at || new Date().toISOString());
      setDraftStatus(data.status || 'sent');
      return data.id;
    }
    const { data } = await api.post(`/customers/${customerId}/contract-pack`, {
      pack_type: packType,
      manual_fields: manualValues,
      auto_overrides: autoOverrides,
      extra_line_items: extraLineItems,
      vat_rate: vatRate,
      battery_spec: batterySpec,
    });
    setPackId(data.id);
    setLastSaved(data.updated_at || new Date().toISOString());
    setDraftStatus(data.status || 'draft');
    return data.id;
  };

  // Whether to prompt the admin to notify the customer of a contract update
  // after regenerating a SENT contract. Confirmation dialog opens after a
  // successful regenerate — never automatic, never fires for draft packs.
  const [notifyPromptOpen, setNotifyPromptOpen] = useState(false);
  const [notifying, setNotifying] = useState(false);

  // Force a regenerate: save the current wizard state and jump to the
  // preview step. Triggered by the explicit "Regenerate Contract PDF"
  // button surfaced on every editing step — lets admins change figures
  // (cost of hardware, VAT, extra line items, battery spec, etc.) and
  // produce a fresh PDF without having to walk through every step.
  //
  // Because the CRM `system_total_cost` is the source of truth for the
  // contract Grand Total, when the admin's line items no longer reconcile
  // with the customer's stored total we first prompt them to confirm the
  // new total. If they accept, we update the customer file then regenerate
  // (so the PDF reflects the new figure); if they decline, we keep the CRM
  // total and regenerate as-is.
  const [costConfirm, setCostConfirm] = useState(null); // { calculated, crm } | null

  const regenerateContract = async () => {
    const cc = await runCostCheck();
    if (cc && cc.mismatch) {
      setCostConfirm({
        calculated: cc.calculated_subtotal,
        crm: cc.crm_total,
        calculatedGrand: cc.calculated_grand_total,
        crmGrand: cc.grand_total,
        vatRate: cc.vat_rate,
      });
      return;
    }
    await doRegenerate(false);
  };

  const doRegenerate = async (updateTotal) => {
    setCostConfirm(null);
    setGenerating(true);
    // Remember the status BEFORE we save — saveDraft will hold it constant
    // for a sent pack (PATCH path) but we want to gate the notify prompt on
    // the originating state in case the admin pressed Regenerate from a
    // draft just before a separate send action.
    const wasSent = packId && draftStatus === 'sent';
    try {
      if (updateTotal && costCheck) {
        await api.post(`/customers/${customerId}/contract-pack/sync-system-total`, {
          new_total: costCheck.calculated_subtotal,
        });
        toast.success(`Customer system total updated to ${Number(costCheck.calculated_subtotal).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}`);
        await runCostCheck();
      }
      const id = await saveDraft();
      const resp = await api.get(`/customers/${customerId}/contract-pack/${id}/preview.pdf?t=${Date.now()}`, {
        responseType: 'blob',
      });
      const blobUrl = await blobToPdfUrl(resp.data);
      setPreviewUrl(blobUrl);
      setStep('preview');
      toast.success('Contract regenerated with your latest changes');
      // Only prompt the notify dialog when the customer already has the
      // share link — i.e. status was 'sent'. Never for fresh drafts.
      if (wasSent) {
        setNotifyPromptOpen(true);
      }
    } catch (e) {
      const detail = e.response?.data instanceof Blob
        ? await e.response.data.text().catch(() => '')
        : (e.response?.data?.detail || e.message || 'Unknown');
      toast.error(`Regenerate failed: ${String(detail).slice(0, 200)}`);
    } finally {
      setGenerating(false);
    }
  };

  const sendUpdateNotice = async () => {
    if (!packId) return;
    setNotifying(true);
    try {
      const r = await api.post(`/customers/${customerId}/contract-pack/${packId}/notify-update`);
      toast.success(`Update notice emailed to ${r.data?.to || 'customer'}`);
      setNotifyPromptOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send update notice');
    } finally {
      setNotifying(false);
    }
  };

  const addExtraLineItem = (preset = null) => {
    const base = preset
      ? {
          category: preset.category,
          description: preset.description,
          qty: preset.qty || 1,
          unit_price: preset.unit_price || '',
          warranty_years: preset.warranty_years || '',
        }
      : { category: 'Additional Hardware', description: '', qty: 1, unit_price: '', warranty_years: '' };
    setExtraLineItems((p) => [...p, base]);
  };
  const updateExtraLineItem = (idx, key, value) => {
    setExtraLineItems((p) => p.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };
  const removeExtraLineItem = (idx) => {
    setExtraLineItems((p) => p.filter((_, i) => i !== idx));
  };

  // Supporting PDF upload — supports multiple files; appends to the list.
  // Saves the draft first if needed so we have a pack_id to attach to.
  const uploadUserPdfs = async (filesList) => {
    if (!filesList?.length) return;
    const files = Array.from(filesList);
    const invalid = files.find((f) => !f.name.toLowerCase().endsWith('.pdf'));
    if (invalid) { toast.error(`"${invalid.name}" is not a PDF`); return; }
    const oversize = files.find((f) => f.size > 30 * 1024 * 1024);
    if (oversize) { toast.error(`"${oversize.name}" exceeds the 30 MB per-file limit`); return; }
    if ((userPdfs.length + files.length) > 5) {
      toast.error(`Maximum 5 supporting PDFs per pack (you have ${userPdfs.length}, tried to add ${files.length})`);
      return;
    }
    setUploadingPdf(true);
    try {
      const pid = packId || (await saveDraft());
      // Upload sequentially so the server-side total-size check sees each one
      const newEntries = [];
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        const { data } = await api.post(`/customers/${customerId}/contract-pack/${pid}/user-pdf`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newEntries.push({
          id: data.file_id,
          filename: data.filename,
          size_bytes: data.size_bytes,
          uploaded_at: new Date().toISOString(),
        });
      }
      setUserPdfs((p) => [...p, ...newEntries]);
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? 's' : ''} — will be stitched into the Supporting Documents section`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF upload failed');
    } finally { setUploadingPdf(false); }
  };

  const removeUserPdf = async (fileId, filename) => {
    if (!packId) { setUserPdfs((p) => p.filter((f) => f.id !== fileId)); return; }
    if (!window.confirm(`Remove "${filename}" from this contract pack?`)) return;
    try {
      await api.delete(`/customers/${customerId}/contract-pack/${packId}/user-pdf/${fileId}`);
      setUserPdfs((p) => p.filter((f) => f.id !== fileId));
      toast.success('Removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove PDF');
    }
  };

  const clearAllUserPdfs = async () => {
    if (!packId) { setUserPdfs([]); return; }
    if (!window.confirm(`Remove all ${userPdfs.length} supporting PDFs from this pack?`)) return;
    try {
      await api.delete(`/customers/${customerId}/contract-pack/${packId}/user-pdf`);
      setUserPdfs([]);
      toast.success('All supporting PDFs removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear PDFs');
    }
  };

  // Drag-to-reorder for supporting PDFs.
  // Uses native HTML5 drag-and-drop — no new deps. Optimistic local reorder,
  // rolls back + toasts on backend failure. Mirrors the order in the final PDF.
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const reorderUserPdfs = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    const next = [...userPdfs];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    const prev = userPdfs;
    setUserPdfs(next);  // optimistic
    if (!packId) return;  // not yet saved — local reorder only
    try {
      await api.put(`/customers/${customerId}/contract-pack/${packId}/user-pdf/reorder`, {
        file_ids: next.map((f) => f.id),
      });
      toast.success('Order updated — supporting PDFs will be stitched in this order');
    } catch (e) {
      setUserPdfs(prev);  // rollback
      toast.error(e.response?.data?.detail || 'Failed to reorder');
    }
  };

  const handlePreview = async () => {
    setGenerating(true);
    try {
      // ALWAYS save the latest wizard state before regenerating so any edits
      // (battery preset, manual fields, extra line items, VAT, etc.) are
      // reflected in the rendered PDF. Previously this was guarded by
      // `packId || saveDraft()` which meant edits made AFTER the first save
      // were silently dropped — including the battery spec table data.
      const id = await saveDraft();
      const resp = await api.get(`/customers/${customerId}/contract-pack/${id}/preview.pdf?t=${Date.now()}`, {
        responseType: 'blob',
      });
      const blobUrl = await blobToPdfUrl(resp.data);
      setPreviewUrl(blobUrl);
      setStep('preview');
    } catch (e) {
      const detail = e.response?.data instanceof Blob
        ? await e.response.data.text().catch(() => '')
        : (e.response?.data?.detail || e.message || 'Unknown');
      toast.error(`Preview failed: ${String(detail).slice(0, 200)}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!packId) {
      toast.error('Save draft first');
      return;
    }
    if (bundleLoa && !bundleLoaDno.trim()) {
      toast.error('Enter a DNO name to bundle the Letter of Authority');
      return;
    }
    if (!cutoutStatus?.has_cutout && !skipCutout) {
      toast.error('Add a cut-out image to the customer, or tick "skip" to collect it at signing');
      return;
    }
    // Guard: customers are prompted to pay their deposit right after signing.
    // If there's no outstanding deposit invoice (and none already paid), warn
    // the admin so the customer isn't left with nothing to pay.
    try {
      const { data: dep } = await api.get(`/customers/${customerId}/deposit-status`);
      if (!dep.has_unpaid_deposit && !dep.deposit_paid) {
        setDepositWarnAmount(dep.deposit_amount || 0);
        setShowDepositWarn(true);
        return;
      }
    } catch {
      // Non-blocking — if the check fails, fall through and send anyway.
    }
    await doSend();
  };

  const doSend = async () => {
    setShowDepositWarn(false);
    setGenerating(true);
    try {
      // Bundle LoA first (separate API) so the sign endpoint picks up the linkage
      if (bundleLoa) {
        try {
          await api.post(`/customers/${customerId}/contract-pack/${packId}/bundle-loa`, {
            dno_name: bundleLoaDno.trim(),
            application_type: bundleLoaAppType,
          });
        } catch (e) {
          const detail = e.response?.data?.detail || '';
          // If already bundled, continue silently — otherwise abort the send
          if (!/already bundled/i.test(detail)) {
            toast.error(detail || 'Failed to bundle Letter of Authority');
            setGenerating(false);
            return;
          }
        }
      }
      const { data } = await api.post(`/customers/${customerId}/contract-pack/${packId}/send`, {
        send_email: true,
        skip_cutout: skipCutout,
      });
      toast.success(
        bundleLoa
          ? 'Contract pack + LoA sent — one signature signs both'
          : 'Contract pack sent — signing link emailed to customer'
      );
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setGenerating(false);
    }
  };

  const openAddDeposit = async () => {
    try {
      const { data } = await api.get(`/customers/${customerId}`);
      setDepositCustomer(data);
      setShowDepositWarn(false);
      setShowDepositDialog(true);
    } catch {
      toast.error('Could not load customer to add a deposit invoice');
    }
  };

  const onDepositDialogClose = async () => {
    setShowDepositDialog(false);
    try {
      const { data: dep } = await api.get(`/customers/${customerId}/deposit-status`);
      if (dep.has_unpaid_deposit || dep.deposit_paid) {
        toast.success('Deposit invoice found — sending the contract now');
        await doSend();
        return;
      }
    } catch { /* noop */ }
    setShowDepositWarn(true);
  };

  const renderInput = (field, value, onChange, extraClass = '') => {
    if (field.type === 'textarea') {
      return <Textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`contract-input-${field.placeholder}`} />;
    }
    if (field.type === 'date') {
      return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`contract-input-${field.placeholder}`} />;
    }
    if (field.type === 'currency') {
      return (
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
          <Input
            type="number" step="0.01" className={`pl-7 transition-colors ${extraClass}`}
            value={value || ''} onChange={(e) => onChange(e.target.value)}
            data-testid={`contract-input-${field.placeholder}`}
          />
        </div>
      );
    }
    if (field.type === 'number') {
      return <Input type="number" step="any" className={`transition-colors ${extraClass}`} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`contract-input-${field.placeholder}`} />;
    }
    return <Input className={`transition-colors ${extraClass}`} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={`contract-input-${field.placeholder}`} />;
  };

  // Sample auto-fill keys to show admin for quick verification
  const keyAutoFields = [
    '[First Name]', '[Last Name]', '[Address Line 1]', '[Postcode]',
    '[Date]', '[Date + 30 Days]', '[Total System Cost]', '[Deposit total]',
    '[Final Balance total]', '[Installation Date]', '[CRM USER]',
    '[Solar Panels Manufacturer]', '[Inverter Manufacturer]', '[Battery Manufacturer]',
    '[Solar Panel Model]', '[Inverter Model]', '[Battery Model]',
    '[Solar Panel qty]', '[Inverter qty]', '[Battery qty]',
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="contract-pack-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-blue-600" />
            Contract Pack — {customerName}
            {jobLabel && (
              <Badge variant="outline" className="text-[11px] border-amber-300 text-amber-800 bg-amber-50" data-testid="contract-wizard-job-label">
                <House className="w-3 h-3 mr-1" /> {jobLabel}
              </Badge>
            )}
            {packId && draftStatus === 'draft' && (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                Editing draft
              </Badge>
            )}
            {packId && draftStatus === 'sent' && (
              <Badge variant="outline" className="text-[10px] border-sky-300 text-sky-700 bg-sky-50">
                Updating sent contract
              </Badge>
            )}
            {packId && (draftStatus === 'customer_signed' || draftStatus === 'fully_signed') && (
              <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                {draftStatus === 'fully_signed' ? 'Fully signed' : 'Customer signed'} — read-only
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Generate, review and send the Helios contract pack for customer signature.
          </DialogDescription>
        </DialogHeader>

        {/* Editing-an-existing-draft banner. Makes it obvious that any
            changes will replace the current draft + that clicking
            Regenerate will rebuild the PDF from the latest figures. */}
        {packId && (draftStatus === 'draft' || draftStatus === 'sent') && step !== 'type' && step !== 'review' && step !== 'preview' && (
          <div
            className={`flex items-start justify-between gap-3 p-2.5 rounded-md border text-xs ${
              draftStatus === 'sent'
                ? 'border-sky-200 bg-sky-50 text-sky-900'
                : 'border-amber-200 bg-amber-50 text-amber-900'
            }`}
            data-testid="draft-edit-banner"
          >
            <div className="flex items-start gap-2 flex-1">
              <RefreshCw className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${draftStatus === 'sent' ? 'text-sky-700' : 'text-amber-700'}`} />
              <span>
                {draftStatus === 'sent' ? (
                  <>
                    You&apos;re editing a contract <strong>already sent</strong> to the customer
                    {lastSaved ? <> · last saved <strong>{new Date(lastSaved).toLocaleString('en-GB')}</strong></> : null}.
                    The existing sign link will automatically serve the updated PDF, and the customer&apos;s view count will reset so a fresh view of the new figures is tracked separately.
                  </>
                ) : (
                  <>
                    You&apos;re editing an existing draft
                    {lastSaved ? <> · last saved <strong>{new Date(lastSaved).toLocaleString('en-GB')}</strong></> : null}.
                    Any change to the figures or supporting documents will replace the current draft when you click <strong>Regenerate Contract PDF</strong>.
                  </>
                )}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={regenerateContract}
              disabled={generating}
              className={`h-7 text-xs bg-white flex-shrink-0 ${
                draftStatus === 'sent'
                  ? 'border-sky-300 text-sky-800 hover:bg-sky-100'
                  : 'border-amber-300 text-amber-800 hover:bg-amber-100'
              }`}
              data-testid="banner-regenerate-btn"
            >
              {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Regenerate
            </Button>
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs flex-wrap">
          {Object.keys(STEP_LABELS).filter((s) => s !== 'battery' || isBatteryPack).map((s, i, arr) => (
            <React.Fragment key={s}>
              <Badge variant={step === s ? 'default' : 'outline'} className={step === s ? 'bg-blue-600' : ''}>
                {i + 1}. {STEP_LABELS[s]}
              </Badge>
              {i < arr.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>

        {/* Proposal upload prompt — only shown when the customer has no
            solar_proposal on file AND the pack will include a Performance
            Estimate. Lets the admin auto-fill 24 MCS fields in one click. */}
        {!loading && needsProposalUpload && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex flex-wrap items-start gap-3" data-testid="wizard-proposal-prompt">
            <Wand2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[260px]">
              <p className="text-sm font-semibold text-amber-900">
                Auto-fill the Performance Estimate (MCS)
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Upload the customer's solar PV proposal and we'll auto-fill 24 MCS fields
                (installed capacity, Kk, shade factor, self-consumption, payback, etc.) — saves the manual typing.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={uploadingProposal}
              onClick={() => proposalInputRef.current?.click()}
              className="bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="wizard-upload-proposal-btn"
            >
              {uploadingProposal ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Reading proposal…</>
              ) : (
                <><Upload className="w-4 h-4 mr-1.5" />Upload proposal PDF</>
              )}
            </Button>
            <input
              ref={proposalInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleProposalUploadInWizard}
              className="hidden"
              data-testid="wizard-proposal-input"
            />
          </div>
        )}

        {/* Confirmation banner once the proposal has been read */}
        {!loading && hasMcsAutoFill && (packType === 'solar_battery' || packType === 'solar_only') && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2 flex items-center gap-2 text-xs text-emerald-800" data-testid="wizard-proposal-applied">
            <CheckCircle2 className="w-4 h-4" />
            Performance Estimate (MCS) auto-filled from the customer's uploaded proposal.
            {' '}You can still edit any value in the Manual Fields step.
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}

        {!loading && step === 'type' && (
          <div className="space-y-4">
            <Label>Which type of contract pack?</Label>
            <Select value={packType} onValueChange={setPackType}>
              <SelectTrigger data-testid="pack-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(PACK_TYPE_LABELS).map((k) => (
                  <SelectItem key={k} value={k}>{PACK_TYPE_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              The pack will contain: Covering Letter, {packType !== 'battery_only' && 'Performance Estimate, '}Quotation, Safety at Home, and Contract of Sale.
            </p>
          </div>
        )}

        {!loading && step === 'review' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              These values were pulled automatically from the customer record. Click to override before generating.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[55vh] overflow-y-auto pr-1">
              {keyAutoFields.map((k) => (autoFill[k] !== undefined && (() => {
                const pill = equipmentPillState(k);
                return (
                  <div key={k} className="space-y-1">
                    <div className="flex items-center gap-1.5 min-h-[18px]">
                      <Label className="text-[11px] text-slate-600 font-normal">{k.replace(/[[\]]/g, '')}</Label>
                      {pill?.kind === 'auto' && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0"
                          title="Pulled from the customer's uploaded solar PV proposal"
                          data-testid={`equip-pill-auto-${k}`}
                        >
                          <Sparkles className="w-2.5 h-2.5" /> Auto-filled
                        </span>
                      )}
                      {pill?.kind === 'edited' && (
                        <>
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0"
                            title={`Edited from extracted value: ${pill.extracted}`}
                            data-testid={`equip-pill-edited-${k}`}
                          >
                            Edited
                          </span>
                          <button
                            type="button"
                            onClick={() => overrideAuto(k, pill.extracted)}
                            className="text-[9px] text-amber-700 hover:text-amber-900 underline underline-offset-2 ml-0.5"
                            title={`Revert to extracted value: ${pill.extracted}`}
                            data-testid={`equip-revert-${k}`}
                          >
                            revert
                          </button>
                        </>
                      )}
                    </div>
                    <Input
                      value={autoOverrides[k] !== undefined ? autoOverrides[k] : autoFill[k]}
                      onChange={(e) => overrideAuto(k, e.target.value)}
                      className="text-sm h-9"
                      data-testid={`auto-${k}`}
                    />
                  </div>
                );
              })()))}
            </div>
          </div>
        )}

        {!loading && step === 'manual' && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <p className="text-xs text-slate-500">
              Please fill in the fields below. Anything you leave blank will appear as an empty space on the final document.
            </p>

            {/* Live cost reconciliation — updates instantly as you type */}
            {liveCost.hasCrm && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1.5" data-testid="live-cost-summary">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">
                    Line items so far:{' '}
                    <strong className={liveCost.mismatch ? 'text-rose-700' : 'text-emerald-700'} data-testid="live-cost-subtotal">
                      {liveCost.subtotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                    </strong>
                  </span>
                  <span className="text-slate-500">
                    Customer total (ex VAT): <strong className="text-slate-700">{liveCost.crmTotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 pt-1.5 text-sm">
                  <span className="font-semibold text-slate-700">
                    Grand Total (inc VAT{liveCost.vr ? ` @ ${Math.round(liveCost.vr * 100)}%` : ''})
                  </span>
                  <strong
                    className={`text-base ${liveCost.mismatch ? 'text-rose-700' : 'text-emerald-700'}`}
                    data-testid="live-grand-total"
                  >
                    {liveCost.liveGrandTotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </strong>
                </div>
                {liveCost.mismatch && (
                  <div className="text-[11px] text-slate-500 text-right" data-testid="live-grand-total-target">
                    Target (customer file): {liveCost.crmGrandTotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </div>
                )}
              </div>
            )}

            {/* Cost discrepancy banner — system_total_cost on the customer wins */}
            {liveCost.hasCrm && liveCost.mismatch && (
              <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3" data-testid="cost-mismatch-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-rose-900">Line-item total doesn't match the customer file</p>
                    <p className="text-xs text-rose-800 mt-1">
                      Your costs add up to <strong>{liveCost.subtotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>,
                      but the customer's <strong>system total</strong> is <strong>{liveCost.crmTotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>
                      {' '}(delta {liveCost.delta.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', signDisplay: 'always' })}).
                      The customer file always wins — the GRAND TOTAL on the quotation will show the customer-file figure.
                      The highlighted cost fields below stay red until they reconcile, then turn green.
                    </p>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={applyCrmTotal}
                        className="h-7 text-xs bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="use-crm-total-btn"
                      >
                        <Wand2 className="w-3 h-3 mr-1" />
                        Use CRM total — set hardware cost to {liveCost.suggested.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {liveCost.hasCrm && !liveCost.mismatch && (
              <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800" data-testid="cost-ok-banner">
                <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                Line-item total matches the customer file ({liveCost.crmTotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}).
              </div>
            )}

            {Object.entries(groupedManualFields).map(([groupKey, group]) => (
              <div key={groupKey} className="space-y-2 border border-slate-200 rounded-lg p-3">
                <h4 className="text-sm font-semibold text-slate-800">{group.label}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {group.fields.map((f) => {
                    const pill = mcsPillState(f.placeholder);
                    return (
                      <div key={f.placeholder} className="space-y-1">
                        <div className="flex items-center gap-1.5 min-h-[18px]">
                          <Label className="text-[11px] text-slate-600 font-normal">{f.label}</Label>
                          {pill?.kind === 'auto' && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0"
                              title="This value was pulled from the customer's uploaded solar PV proposal"
                              data-testid={`mcs-pill-auto-${f.placeholder}`}
                            >
                              <Sparkles className="w-2.5 h-2.5" /> Auto-filled
                            </span>
                          )}
                          {pill?.kind === 'edited' && (
                            <>
                              <span
                                className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0"
                                title={`Edited from extracted value: ${pill.extracted}`}
                                data-testid={`mcs-pill-edited-${f.placeholder}`}
                              >
                                Edited
                              </span>
                              <button
                                type="button"
                                onClick={() => setManual(f.placeholder, pill.extracted)}
                                className="text-[9px] text-amber-700 hover:text-amber-900 underline underline-offset-2 ml-0.5"
                                title={`Revert to extracted value: ${pill.extracted}`}
                                data-testid={`mcs-revert-${f.placeholder}`}
                              >
                                revert
                              </button>
                            </>
                          )}
                        </div>
                        {renderInput(f, manualValues[f.placeholder], (v) => setManual(f.placeholder, v), costFieldClass(f.placeholder))}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Additional Hardware — extra line items shown in the equipment schedule */}
            <div className="space-y-2 border border-violet-200 bg-violet-50/40 rounded-lg p-3" data-testid="extra-hardware-section">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-violet-900">Additional Hardware</h4>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => addExtraLineItem()}
                  className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-100"
                  data-testid="add-extra-hardware-btn"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add line
                </Button>
              </div>
              <p className="text-[11px] text-violet-700/80">
                Anything you add here is itemised on the Quotation page AND given its own warranty line on the contract.
              </p>

              {/* Preset quick-add chips */}
              {presets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 py-1">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wide self-center mr-1">Quick add:</span>
                  {presets.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addExtraLineItem(p)}
                      className="text-[11px] px-2 py-1 rounded border border-violet-200 bg-white text-violet-700 hover:bg-violet-100"
                      data-testid={`quick-add-${p.id}`}
                      title={`${p.description} · £${Number(p.unit_price||0).toFixed(2)}`}
                    >
                      + {p.description.length > 32 ? p.description.slice(0, 32) + '…' : p.description}
                    </button>
                  ))}
                </div>
              )}

              {extraLineItems.length === 0 && (
                <p className="text-xs text-slate-400 italic py-1">No additional hardware added yet.</p>
              )}
              {extraLineItems.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white rounded border border-slate-200 p-2.5" data-testid={`extra-row-${idx}`}>
                  <div className="col-span-12 md:col-span-3">
                    <Label className="text-[10px] text-slate-500 font-normal">Category</Label>
                    <Input
                      value={it.category || ''}
                      onChange={(e) => updateExtraLineItem(idx, 'category', e.target.value)}
                      placeholder="e.g. EV Charger"
                      className="h-8 text-sm"
                      data-testid={`extra-category-${idx}`}
                    />
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <Label className="text-[10px] text-slate-500 font-normal">Description</Label>
                    <Input
                      value={it.description || ''}
                      onChange={(e) => updateExtraLineItem(idx, 'description', e.target.value)}
                      placeholder="e.g. MyEnergi Zappi 7kW tethered"
                      className="h-8 text-sm"
                      data-testid={`extra-desc-${idx}`}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-1">
                    <Label className="text-[10px] text-slate-500 font-normal">Qty</Label>
                    <Input
                      type="number" min="1"
                      value={it.qty || 1}
                      onChange={(e) => updateExtraLineItem(idx, 'qty', e.target.value)}
                      className="h-8 text-sm"
                      data-testid={`extra-qty-${idx}`}
                    />
                  </div>
                  <div className="col-span-5 md:col-span-2">
                    <Label className="text-[10px] text-slate-500 font-normal">Unit Price (£)</Label>
                    <Input
                      type="number" step="0.01"
                      value={it.unit_price || ''}
                      onChange={(e) => updateExtraLineItem(idx, 'unit_price', e.target.value)}
                      className="h-8 text-sm"
                      data-testid={`extra-price-${idx}`}
                    />
                  </div>
                  <div className="col-span-3 md:col-span-1">
                    <Label className="text-[10px] text-slate-500 font-normal">Warranty (yrs)</Label>
                    <Input
                      type="number" min="0"
                      value={it.warranty_years || ''}
                      onChange={(e) => updateExtraLineItem(idx, 'warranty_years', e.target.value)}
                      className="h-8 text-sm"
                      data-testid={`extra-warr-${idx}`}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button
                      type="button" size="sm" variant="ghost"
                      onClick={() => removeExtraLineItem(idx)}
                      className="h-8 w-8 p-0 text-rose-500 hover:bg-rose-50"
                      data-testid={`extra-remove-${idx}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* VAT toggle */}
            <div className="space-y-2 border border-sky-200 bg-sky-50/40 rounded-lg p-3" data-testid="vat-section">
              <h4 className="text-sm font-semibold text-sky-900">VAT</h4>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setVatRate(0)}
                  className={`flex-1 text-xs px-3 py-2 rounded border ${vatRate === 0 ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                  data-testid="vat-0-btn"
                >
                  0% (Domestic — UK 0% rating until 2027)
                </button>
                <button
                  type="button"
                  onClick={() => setVatRate(0.20)}
                  className={`flex-1 text-xs px-3 py-2 rounded border ${vatRate > 0 ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                  data-testid="vat-20-btn"
                >
                  20% (Commercial / Standard rate)
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && step === 'battery' && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="battery-spec-step">
            <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3">
              <Battery className="w-5 h-5 text-sky-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-sky-900">Battery Storage Technical Information (MIS 3012)</p>
                <p className="text-xs text-sky-800/90 mt-0.5">
                  This table appears on every battery / solar+battery quotation.
                  Pick the customer's battery from the dropdown below and the 14 fields will
                  auto-fill — or choose <strong>“Enter manually”</strong> if it's not on the list.
                  Tip: save common batteries as presets at <strong>Admin → Contract Setup → Battery Presets</strong>.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1" data-testid="battery-preset-picker">
              <Label htmlFor="battery-preset-select" className="text-xs font-semibold text-slate-700 shrink-0">
                Battery preset:
              </Label>
              <Select
                value={selectedPresetId || '__manual__'}
                onValueChange={(v) => {
                  if (v === '__manual__') {
                    setSelectedPresetId('');
                    setBatterySpec({});
                    toast.info('Cleared — enter battery specs manually below');
                  } else {
                    const preset = batteryPresets.find((p) => p.id === v);
                    if (preset) {
                      applyBatteryPreset(preset);
                      setSelectedPresetId(v);
                    }
                  }
                }}
              >
                <SelectTrigger
                  id="battery-preset-select"
                  className="h-9 text-sm flex-1 max-w-md"
                  data-testid="battery-preset-select"
                >
                  <SelectValue placeholder="Select a battery from your presets…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__manual__" data-testid="battery-preset-manual">
                    <span className="text-slate-500 italic">— Enter manually —</span>
                  </SelectItem>
                  {batteryPresets.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`battery-preset-${b.id}`}>
                      {b.manufacturer} {b.model}
                      {b.usable_capacity_kwh ? ` · ${b.usable_capacity_kwh}kWh` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {batteryPresets.length === 0 && (
                <span className="text-[11px] text-amber-700 italic">
                  No presets yet — add some at <strong>Admin → Contract Setup → Battery Presets</strong>
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {BATTERY_SPEC_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-[11px] text-slate-600 font-normal">{f.label}</Label>
                  {f.type === 'select' ? (
                    <Select
                      value={batterySpec[f.key] || ''}
                      onValueChange={(v) => setBatterySpec((p) => ({ ...p, [f.key]: v }))}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid={`battery-${f.key}`}>
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={batterySpec[f.key] || ''}
                      onChange={(e) => setBatterySpec((p) => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder || ''}
                      className="h-9 text-sm"
                      data-testid={`battery-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && step === 'upload' && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1" data-testid="upload-step">
            <div className="flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50 p-3">
              <Paperclip className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-violet-900">Supporting PDFs (optional)</p>
                <p className="text-xs text-violet-800/90 mt-0.5">
                  Attach a site survey, custom diagram, manufacturer spec sheet — anything the
                  customer should see alongside the quote. Multiple files are stitched together
                  and inserted into the pack <strong>after the Covering Letter</strong> and{' '}
                  <strong>before the Quotation</strong>. Up to 5 files, 30&nbsp;MB each, 150&nbsp;MB total.
                </p>
              </div>
            </div>

            {userPdfs.length > 0 && (
              <div className="space-y-1.5" data-testid="user-pdfs-list">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-700">
                    {userPdfs.length} file{userPdfs.length > 1 ? 's' : ''} attached
                    {userPdfs.length > 1 && (
                      <span className="ml-2 font-normal text-slate-500">· drag to reorder</span>
                    )}
                  </p>
                  <Button size="sm" variant="ghost" onClick={clearAllUserPdfs} className="h-7 text-[11px] text-rose-600 hover:bg-rose-50" data-testid="clear-all-pdfs">
                    Remove all
                  </Button>
                </div>
                {userPdfs.map((f, idx) => (
                  <div
                    key={f.id}
                    draggable={userPdfs.length > 1}
                    onDragStart={(e) => {
                      setDragIndex(idx);
                      e.dataTransfer.effectAllowed = 'move';
                      // Required for Firefox to initiate the drag
                      try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* noop */ }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dragOverIndex !== idx) setDragOverIndex(idx);
                    }}
                    onDragLeave={() => { if (dragOverIndex === idx) setDragOverIndex(null); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragIndex;
                      setDragIndex(null);
                      setDragOverIndex(null);
                      if (from != null) reorderUserPdfs(from, idx);
                    }}
                    onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                    className={`rounded-lg border bg-emerald-50 p-2.5 flex items-center gap-3 transition-all ${
                      dragOverIndex === idx && dragIndex !== idx
                        ? 'border-violet-400 ring-2 ring-violet-200'
                        : 'border-emerald-200'
                    } ${dragIndex === idx ? 'opacity-50' : ''}`}
                    data-testid={`user-pdf-row-${f.id}`}
                  >
                    {userPdfs.length > 1 && (
                      <span
                        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 shrink-0"
                        title="Drag to reorder"
                        data-testid={`drag-handle-${f.id}`}
                      >
                        <GripVertical className="w-4 h-4" />
                      </span>
                    )}
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 text-[10px] font-bold text-emerald-700">
                      {idx + 1}
                    </div>
                    <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{f.filename}</p>
                      <p className="text-[11px] text-emerald-700/80">
                        {f.size_bytes ? `${(f.size_bytes / 1024).toFixed(1)} KB` : ''}
                        {f.uploaded_at ? ` · ${new Date(f.uploaded_at).toLocaleString('en-GB')}` : ''}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeUserPdf(f.id, f.filename)} className="h-7 text-xs text-rose-600 hover:bg-rose-50" data-testid={`remove-pdf-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {userPdfs.length < 5 && (
              <label
                htmlFor="user-pdf-input"
                className="block border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-violet-400 hover:bg-violet-50/30 transition-colors"
                data-testid="user-pdf-dropzone"
              >
                {uploadingPdf ? (
                  <div className="space-y-1">
                    <Loader2 className="w-8 h-8 text-violet-500 mx-auto animate-spin" />
                    <p className="text-sm text-slate-600">Uploading…</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto" />
                    <p className="text-sm text-slate-700 font-medium">
                      {userPdfs.length === 0 ? 'Click to choose PDF(s)' : 'Add another PDF'}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      Select multiple files to upload them all in one go
                    </p>
                  </div>
                )}
              </label>
            )}

            <input
              id="user-pdf-input"
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="hidden"
              onChange={(e) => { uploadUserPdfs(e.target.files); e.target.value = ''; }}
              data-testid="user-pdf-input"
            />
          </div>
        )}

        {!loading && step === 'preview' && (
          <div className="space-y-3">
            {previewUrl ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs text-slate-500 flex-1 min-w-[200px]">
                    Large contracts (with a full solar proposal) can be slow to show inline. If the preview below is blank or says it can&apos;t load, use <strong>Open in new tab</strong> or <strong>Download</strong>.
                  </p>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}
                      data-testid="contract-preview-open-tab"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in new tab
                    </Button>
                    <Button type="button" size="sm" variant="outline" asChild data-testid="contract-preview-download">
                      <a href={previewUrl} download={`ContractPack-${(customerId || 'preview').slice(0, 8)}.pdf`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Download
                      </a>
                    </Button>
                  </div>
                </div>
                <iframe src={previewUrl} className="w-full h-[60vh] border rounded" title="Contract pack preview" data-testid="contract-preview-iframe" />
              </div>
            ) : (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Generating PDF…
              </div>
            )}

            {/* Cut-out image gate — required before sending */}
            {cutoutStatus && !cutoutStatus.has_cutout && (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-3 space-y-2.5" data-testid="cutout-required-banner">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-rose-900">Cut-out image required</div>
                    <div className="text-xs text-rose-700 mt-0.5">
                      This contract cannot be sent to the customer until a cut-out image is added to their documents.
                      You can still save it as a draft — it'll wait on the Contracts page until the cut-out is added.
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pl-7">
                  <Button
                    type="button" size="sm" variant="outline"
                    className="border-rose-300 text-rose-700 hover:bg-rose-100"
                    disabled={uploadingCutout}
                    onClick={() => cutoutInputRef.current?.click()}
                    data-testid="cutout-upload-btn"
                  >
                    {uploadingCutout ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    Upload cut-out image
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    className="border-rose-300 text-rose-700 hover:bg-rose-100"
                    disabled={!(cutoutStatus.image_documents?.length)}
                    onClick={() => setCutoutPickerOpen(true)}
                    data-testid="cutout-pick-btn"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" />
                    Choose from documents
                  </Button>
                  <input
                    ref={cutoutInputRef} type="file" accept="image/*" className="hidden"
                    onChange={handleUploadCutout} data-testid="cutout-file-input"
                  />
                </div>
                <label className="flex items-start gap-2 pl-7 pt-1 cursor-pointer" data-testid="cutout-skip-toggle">
                  <input
                    type="checkbox"
                    checked={skipCutout}
                    onChange={(e) => setSkipCutout(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-rose-300 text-rose-600"
                    data-testid="cutout-skip-checkbox"
                  />
                  <span className="text-xs text-rose-800">
                    Skip for now — I'll collect the cut-out from the customer when they sign the contract.
                    <span className="block text-rose-600/80">The customer is asked for it during signing and chased in their portal.</span>
                  </span>
                </label>
              </div>
            )}
            {cutoutStatus?.has_cutout && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2 flex items-center gap-2 text-xs text-emerald-800" data-testid="cutout-present-banner">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                Cut-out image on file{cutoutStatus.filename ? ` — ${cutoutStatus.filename}` : ''}.
              </div>
            )}

            {/* Bundle a Letter of Authority — one signature covers both docs */}
            <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-2.5">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox
                  checked={bundleLoa}
                  onCheckedChange={(v) => setBundleLoa(!!v)}
                  data-testid="wizard-bundle-loa-checkbox"
                  className="mt-0.5"
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-amber-900">Bundle a Letter of Authority with this contract</div>
                  <div className="text-xs text-amber-700 mt-0.5">
                    The customer's signature will cover BOTH documents. Both still appear separately on the Contracts page for audit.
                  </div>
                </div>
              </label>
              {bundleLoa && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-7">
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold block mb-1">DNO</label>
                    <Input
                      value={bundleLoaDno}
                      onChange={(e) => setBundleLoaDno(e.target.value)}
                      placeholder="e.g. GTC, UKPN, SSEN"
                      className="h-8 text-xs"
                      data-testid="wizard-bundle-loa-dno"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold block mb-1">App. type</label>
                    <select
                      value={bundleLoaAppType}
                      onChange={(e) => setBundleLoaAppType(e.target.value)}
                      className="h-8 text-xs border border-slate-200 rounded px-2 w-full bg-white"
                      data-testid="wizard-bundle-loa-apptype"
                    >
                      <option value="G99">G99</option>
                      <option value="G98">G98</option>
                      <option value="ENA Notification">ENA Notification</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
              When you click <strong>Send for Signing</strong>, the customer will be emailed a secure 21-day signing link.
              Once they've signed, you'll be notified to counter-sign in the customer file.
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="contract-cancel">Cancel</Button>
          {step === 'type' && (
            <Button onClick={() => setStep('review')} data-testid="contract-next-review">
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 'review' && (
            <>
              <Button variant="outline" onClick={() => setStep('type')}>Back</Button>
              <Button onClick={() => setStep('manual')} data-testid="contract-next-manual">
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 'manual' && (
            <>
              <Button variant="outline" onClick={() => setStep('review')}>Back</Button>
              {packId && (
                <Button
                  variant="outline"
                  onClick={regenerateContract}
                  disabled={generating}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="contract-regenerate-manual-btn"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Regenerate Contract PDF
                </Button>
              )}
              {isBatteryPack ? (
                <Button onClick={() => setStep('battery')} data-testid="contract-next-battery">
                  Continue to Battery Spec <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={() => setStep('upload')} data-testid="contract-next-upload">
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </>
          )}
          {step === 'battery' && (
            <>
              <Button variant="outline" onClick={() => setStep('manual')}>Back</Button>
              {packId && (
                <Button
                  variant="outline"
                  onClick={regenerateContract}
                  disabled={generating}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="contract-regenerate-battery-btn"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Regenerate Contract PDF
                </Button>
              )}
              <Button onClick={() => setStep('upload')} data-testid="contract-next-upload">
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
          {step === 'upload' && (
            <>
              <Button variant="outline" onClick={() => setStep(isBatteryPack ? 'battery' : 'manual')}>Back</Button>
              <Button onClick={handlePreview} disabled={generating} data-testid="contract-preview-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : packId ? <RefreshCw className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {packId ? 'Regenerate Contract PDF' : 'Generate Contract PDF'}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('upload')}>Back to fields</Button>
              <Button variant="outline" onClick={handlePreview} disabled={generating}>
                <Eye className="w-4 h-4 mr-1" /> Regenerate
              </Button>
              <Button
                onClick={handleSend}
                disabled={generating || !packId || (!cutoutStatus?.has_cutout && !skipCutout)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="contract-send-btn"
                title={(!cutoutStatus?.has_cutout && !skipCutout) ? 'Add a cut-out image, or tick "skip" to collect it at signing' : undefined}
              >
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Send for Signing
              </Button>
            </>
          )}
        </DialogFooter>

        {/* Cut-out image picker — choose an existing customer photo */}
        <Dialog open={cutoutPickerOpen} onOpenChange={setCutoutPickerOpen}>
          <DialogContent className="sm:max-w-2xl" data-testid="cutout-picker-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-rose-600" />
                Choose the cut-out image
              </DialogTitle>
              <DialogDescription>
                Pick one of the customer's existing photos to set as the cut-out image.
              </DialogDescription>
            </DialogHeader>
            {cutoutStatus?.image_documents?.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto">
                {cutoutStatus.image_documents.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => handlePickCutout(img.id)}
                    className="group relative border rounded overflow-hidden hover:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-400"
                    data-testid={`cutout-pick-${img.id}`}
                  >
                    <img
                      src={`${api.defaults.baseURL?.replace(/\/api$/, '') || ''}${img.preview_url}`}
                      alt={img.filename}
                      className="w-full h-28 object-cover bg-slate-100"
                    />
                    <div className="px-2 py-1 text-[11px] truncate text-left">{img.filename}</div>
                    {img.is_cutout && (
                      <span className="absolute top-1 right-1 bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded">Current</span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 py-6 text-center">No image documents on this customer yet. Upload one instead.</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Notify-customer-of-update prompt — only opens after a successful
            regenerate of a SENT contract pack. Never auto-fires. */}
        <Dialog open={notifyPromptOpen} onOpenChange={setNotifyPromptOpen}>
          <DialogContent className="sm:max-w-md" data-testid="notify-update-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-sky-600" />
                Notify customer of the update?
              </DialogTitle>
              <DialogDescription>
                The customer&apos;s existing sign link still works — clicking it will show the new figures. Send a one-line heads-up email so they aren&apos;t surprised by changed numbers?
              </DialogDescription>
            </DialogHeader>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
              Uses the <strong>Contract Pack — Updated Notice</strong> template — editable in <em>Admin → Email Templates</em>.
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNotifyPromptOpen(false)} disabled={notifying} data-testid="notify-update-skip">
                Don&apos;t notify
              </Button>
              <Button
                onClick={sendUpdateNotice}
                disabled={notifying}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="notify-update-send"
              >
                {notifying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Send update notice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cost reconciliation prompt — opens when the wizard line items no
            longer match the customer's CRM system total. The CRM total is the
            contract Grand Total, so we ask the admin to confirm updating it
            before regenerating. */}
        <Dialog open={!!costConfirm} onOpenChange={(v) => { if (!v) setCostConfirm(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="cost-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-amber-600" />
                Update the contract total?
              </DialogTitle>
              <DialogDescription>
                Your line items add up to a different figure than the customer&apos;s current system total. The contract&apos;s Grand Total is taken from the customer file — choose which total to use.
              </DialogDescription>
            </DialogHeader>
            {costConfirm && (
              <div className="space-y-2 text-sm">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-1" data-testid="cost-confirm-new">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Your line items (ex VAT)</span>
                    <strong className="text-amber-700">{Number(costConfirm.calculated).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>
                  </div>
                  <div className="flex items-center justify-between text-amber-800 border-t border-amber-200 pt-1">
                    <span className="font-medium">New Grand Total (inc VAT{costConfirm.vatRate ? ` @ ${Math.round(costConfirm.vatRate * 100)}%` : ''})</span>
                    <strong data-testid="cost-confirm-new-grand">{Number(costConfirm.calculatedGrand).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>
                  </div>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1" data-testid="cost-confirm-current">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Current system total (ex VAT)</span>
                    <strong className="text-slate-700">{Number(costConfirm.crm).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>
                  </div>
                  <div className="flex items-center justify-between text-slate-700 border-t border-slate-200 pt-1">
                    <span className="font-medium">Current Grand Total (inc VAT{costConfirm.vatRate ? ` @ ${Math.round(costConfirm.vatRate * 100)}%` : ''})</span>
                    <strong data-testid="cost-confirm-current-grand">{Number(costConfirm.crmGrand).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</strong>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button
                onClick={() => doRegenerate(true)}
                disabled={generating}
                className="w-full bg-amber-600 hover:bg-amber-700"
                data-testid="cost-confirm-update-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Update customer total &amp; regenerate
              </Button>
              <Button
                variant="outline"
                onClick={() => doRegenerate(false)}
                disabled={generating}
                className="w-full"
                data-testid="cost-confirm-keep-btn"
              >
                Keep current total &amp; regenerate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>

      {/* Deposit-invoice guard — warns before sending if there's nothing for
          the customer to pay after signing. */}
      <Dialog open={showDepositWarn} onOpenChange={setShowDepositWarn}>
        <DialogContent className="sm:max-w-lg" data-testid="deposit-warn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> No deposit invoice found
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-1">
              <span className="block">
                This customer has no outstanding deposit invoice. After they sign, they're
                prompted to pay their deposit{depositWarnAmount ? ` of £${Number(depositWarnAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''} —
                but without an invoice there will be nothing for them to pay.
              </span>
              <span className="block">
                Add a deposit invoice now (create one in Xero or link an existing one), or send
                the contract without it.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowDepositWarn(false); doSend(); }}
              disabled={generating}
              data-testid="deposit-warn-send-anyway-btn"
            >
              Send without deposit invoice
            </Button>
            <Button
              onClick={openAddDeposit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="deposit-warn-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add deposit invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showDepositDialog && depositCustomer && (
        <PostCreateInvoiceDialog
          open={showDepositDialog}
          customer={depositCustomer}
          defaultInvoiceType="deposit"
          triggerLabel="Add a deposit invoice before sending"
          onClose={onDepositDialogClose}
        />
      )}
    </Dialog>
  );
};

export default ContractPackWizard;
